<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import { VButton } from '@/modules/shared/components'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useBuilderStore } from '../stores/builder-store'
import type { ApplicationHeading } from '../types'

const props = defineProps<{ headings: ApplicationHeading[]; selectedHeadingId: number | null }>()
const emit = defineEmits<{ select: [headingId: number] }>()
const builderStore = useBuilderStore()
const notificationStore = useNotificationStore()

const localHeadings = ref<ApplicationHeading[]>([...props.headings])
watch(
  () => props.headings,
  (next) => {
    localHeadings.value = [...next]
    // a refetch can drop the heading being edited (deleted in another session,
    // template replaced) — close the orphaned editor, or the row renders as a
    // plain row while '+ Add heading' stays hidden with no way back
    if (editorMode.value === 'editing' && editingId.value !== null && !next.some((h) => h.id === editingId.value)) {
      closeEditor()
    }
  },
)

// Legacy headings nav: the whole row is draggable (Sortable without a handle
// option); interactive children are filtered out so editing and clicking keep
// working. The new order persists through the sort endpoint, which only
// rewrites heading positions — questions stay attached to their heading.
const onDragEnd = () => {
  const draggedIds = localHeadings.value.map((h) => h.id)
  const serverIds = props.headings.map((h) => h.id)
  // dropping a heading back in place still fires end — skip the no-op POST
  if (draggedIds.every((id, index) => id === serverIds[index])) return

  builderStore.sortHeadings(draggedIds).catch(() => undefined /* error already surfaced via { silent: false } */)
}

///////////////////////////////////////////////////////////////////////////////
// Editor — exactly one heading editor at a time (tickets/3064): 'creating'
// (blank form at the card bottom) or 'editing' (inline on a row). Nothing
// persists until the primary action is clicked; Cancel always returns to the
// idle list view, which also brings back the '+ Add heading' affordance.
///////////////////////////////////////////////////////////////////////////////

type EditorMode = 'idle' | 'creating' | 'editing'
const editorMode = ref<EditorMode>('idle')
const editingId = ref<number | null>(null)
const name = ref('')
const description = ref('')
const saving = ref(false)
const createNameInput = ref<HTMLInputElement | null>(null)
const editNameInputs = ref<HTMLInputElement[]>([])

const startEditor = async (mode: EditorMode, heading?: ApplicationHeading) => {
  editorMode.value = mode
  editingId.value = mode === 'editing' ? (heading?.id ?? null) : null
  name.value = heading?.name ?? ''
  description.value = heading?.description ?? ''
  await nextTick()
  if (mode === 'creating') {
    createNameInput.value?.focus()
  } else {
    editNameInputs.value[0]?.focus()
  }
}

const closeEditor = () => {
  editorMode.value = 'idle'
  editingId.value = null
}

// A clean editor can be swapped instantly; a dirty one asks first — the same
// confirmation pattern the delete actions already use in this component
const editorIsDirty = () => {
  if (editorMode.value === 'creating') return name.value.trim() !== '' || description.value.trim() !== ''
  const heading = props.headings.find((h) => h.id === editingId.value)
  return heading !== undefined && (name.value !== heading.name || description.value !== (heading.description ?? ''))
}

const startCreate = async () => {
  if (saving.value) return
  if (editorMode.value !== 'idle' && editorIsDirty() && !window.confirm('Discard unsaved changes to this heading?'))
    return

  await startEditor('creating')
}

const startEdit = async (heading: ApplicationHeading) => {
  if (saving.value) return
  if (editorMode.value === 'editing' && editingId.value === heading.id) return
  if (editorMode.value !== 'idle' && editorIsDirty() && !window.confirm('Discard unsaved changes to this heading?'))
    return

  await startEditor('editing', heading)
}

const createHeading = async () => {
  const trimmedName = name.value.trim()
  if (!trimmedName || saving.value) return

  saving.value = true
  try {
    const heading = await builderStore.createHeading(trimmedName, description.value.trim())
    emit('select', heading.id)
    closeEditor()
    notificationStore.enqueue('success', 'Heading was successfully created.')
  } catch {
    // error already surfaced via { silent: false } — keep the form open with
    // the typed values so nothing is lost
  } finally {
    saving.value = false
  }
}

const saveEdit = async () => {
  const id = editingId.value
  const trimmedName = name.value.trim()
  if (id === null || !trimmedName || saving.value) return

  saving.value = true
  try {
    await builderStore.updateHeading(id, trimmedName, description.value)
    // legacy update.js.erb enqueued 'Heading was successfully saved.' — the
    // editor closes on save, so the toast carries the confirmation
    notificationStore.enqueue('success', 'Heading was successfully saved.')
    closeEditor()
  } catch {
    // error already surfaced via { silent: false } — keep the form open with
    // the typed values so nothing is lost
  } finally {
    saving.value = false
  }
}

const removeHeading = async (heading: ApplicationHeading) => {
  if (!window.confirm(`Delete "${heading.name}" and all of its questions? This cannot be undone.`)) return

  await builderStore.destroyHeading(heading.id)
}
</script>

<template>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <!-- overflow-hidden clips the selected row's tint to the card's rounded
         corners on the first/last/only row, and the card's own shadow-sm
         draws outside the border box so it is unaffected (tickets/3064).
         The comment lives inside the root to keep the template single-root. -->
    <!-- Card header: the create affordance applies to the whole collection, so
         it sits beside the section title. It hides while an editor is open so
         exactly one primary action is on screen at a time (tickets/3064) -->
    <div class="flex items-center justify-between border-b border-gray-100 px-3 py-2">
      <h3 class="text-sm font-semibold text-gray-900">Headings</h3>
      <VButton v-if="editorMode === 'idle'" variant="secondary" size="xs" @click="startCreate"> + Add heading </VButton>
    </div>

    <draggable
      v-model="localHeadings"
      item-key="id"
      :animation="150"
      filter="input, textarea, button"
      :preventOnFilter="false"
      class="divide-y divide-gray-100"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <div
          class="group flex items-center gap-2 px-3 py-2.5"
          :class="element.id === selectedHeadingId ? 'bg-[#3b88af]/10' : 'hover:bg-gray-50'"
          :aria-current="element.id === selectedHeadingId ? 'true' : undefined"
        >
          <!-- Selected state is tint + semibold name only — no indicator bar
               (tickets/3064). The bg-[#3b88af]/10 tint is the app's
               master-detail idiom (stage-sidebar, sidenav sub-items, filing
               tree); the name stays text-gray-900 rather than turning blue —
               #3b88af on the tint is ~3.5:1, below AA for normal-size text,
               while gray-900 is ~15:1. The card-level overflow-hidden keeps
               the tint inside the rounded corners on the first/last/only row.
               aria-current (not aria-selected: widget roles forbid these
               rows' interactive children) and the bold name keep the state
               legible without relying on color alone. Row buttons carry their
               own keyboard-focus ring so focus never reads as selection. -->
          <!-- Stable grab cursor: fixed-size flex hit box (no falling through
               inline-text glyph gaps), no text selection, pointer events only
               while revealed so the invisible handle cannot flip the cursor -->
          <span
            class="drag-handle pointer-events-none flex h-6 w-4 cursor-grab select-none items-center justify-center text-gray-300 opacity-0 transition-opacity active:cursor-grabbing group-hover:pointer-events-auto group-hover:opacity-100"
            title="Drag to reorder"
            >⋮⋮</span
          >

          <template v-if="editorMode === 'editing' && editingId === element.id">
            <div class="flex w-full flex-col gap-2 py-1">
              <div class="field-with-bar">
                <label class="mb-1 block text-xs font-medium text-gray-500">Heading Name</label>
                <input
                  ref="editNameInputs"
                  v-model="name"
                  type="text"
                  class="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none focus:border-[#3b88af]"
                  @keyup.enter="saveEdit"
                  @keyup.esc="closeEditor"
                />
                <span class="bar" aria-hidden="true"></span>
              </div>
              <div class="field-with-bar">
                <label class="mb-1 block text-xs font-medium text-gray-500">Heading Description</label>
                <textarea
                  v-model="description"
                  rows="2"
                  class="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none focus:border-[#3b88af]"
                  placeholder="Add a description for this heading (optional)"
                  @keyup.esc="closeEditor"
                />
                <span class="bar" aria-hidden="true"></span>
              </div>
              <div class="flex justify-end gap-2">
                <VButton variant="secondary" size="xs" @click="closeEditor">Cancel</VButton>
                <VButton variant="v-blue" size="xs" :disabled="!name.trim()" :loading="saving" @click="saveEdit">
                  Save
                </VButton>
              </div>
            </div>
          </template>

          <template v-else>
            <button
              class="min-w-0 flex-1 truncate rounded-md text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b88af]/60"
              :title="element.name"
              @click="emit('select', element.id)"
            >
              <span :class="element.id === selectedHeadingId ? 'font-semibold text-gray-900' : 'text-gray-700'">
                {{ element.name }}
              </span>
              <span class="ml-1 text-xs text-gray-400">({{ element.questions.length }})</span>
            </button>
            <span
              class="flex items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
            >
              <button
                class="rounded-md text-xs text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b88af]/60"
                title="Edit heading name and description"
                @click="startEdit(element)"
              >
                Edit
              </button>
              <button
                class="rounded-md text-xs text-red-400 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b88af]/60"
                title="Delete section"
                @click="removeHeading(element)"
              >
                Delete
              </button>
            </span>
          </template>
        </div>
      </template>
    </draggable>

    <!-- Create form: no record exists until 'Create heading' is clicked, so
         Cancel is a true no-op (tickets/3064) -->
    <div v-if="editorMode === 'creating'" class="border-t border-gray-100 p-3">
      <div class="flex flex-col gap-2 py-1">
        <div class="field-with-bar">
          <label class="mb-1 block text-xs font-medium text-gray-500">Heading Name</label>
          <input
            ref="createNameInput"
            v-model="name"
            type="text"
            class="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none focus:border-[#3b88af]"
            @keyup.enter="createHeading"
            @keyup.esc="closeEditor"
          />
          <span class="bar" aria-hidden="true"></span>
        </div>
        <div class="field-with-bar">
          <label class="mb-1 block text-xs font-medium text-gray-500">Heading Description</label>
          <textarea
            v-model="description"
            rows="2"
            class="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none focus:border-[#3b88af]"
            placeholder="Add a description for this heading (optional)"
            @keyup.esc="closeEditor"
          />
          <span class="bar" aria-hidden="true"></span>
        </div>
        <div class="flex justify-end gap-2">
          <VButton variant="secondary" size="xs" @click="closeEditor">Cancel</VButton>
          <VButton variant="v-blue" size="xs" :disabled="!name.trim()" :loading="saving" @click="createHeading">
            Create heading
          </VButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Legacy builder field treatment, ported from the Rails input-with-bar
   helpers (paper_inputs.css.scss): a 2px green underline whose halves expand
   from the center while the field is focused. */
.field-with-bar .bar {
  position: relative;
  display: block;
  width: 100%;
}

.field-with-bar .bar::before,
.field-with-bar .bar::after {
  content: '';
  position: absolute;
  bottom: 0;
  height: 2px;
  width: 0;
  background: #008d52; /* $true-green */
  transition: 0.2s ease all;
}

.field-with-bar .bar::before {
  left: 50%;
}

.field-with-bar .bar::after {
  right: 50%;
}

.field-with-bar:focus-within .bar::before,
.field-with-bar:focus-within .bar::after {
  width: 50%;
}
</style>
